import {
  formatPhoneNumber,
  TwilioVerificationChannelEnum,
  messages,
  APIResponse,
  useUserQuery,
  User,
  useTwilioCheckCodeMutation,
  TwilioCheckCodeCreateAccountValues,
  TwilioCheckCodeSendValues,
  useTwilioSendCodeMutation,
} from '@alluradirect/react';
import { Typography } from '@mui/material';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { StepperControlsFormik } from 'components/core/navigation/DynamicStepper/components/StepperControlsFormik';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { verifyImage } from 'config/imageUrls';
import { ButtonWrapper } from '../styles';
import Grid from 'components/core/Grid';
import { useMemo } from 'react';
import { VerificationStepEnum } from 'components/views/verification/VerificationDialog';
import { VerifyCodeInput } from 'components/views/verification/components/VerifyCodeInput';
import React from 'react';
import * as Sentry from '@sentry/nextjs';

const initialValues = { code: '' };

export type VerifyCodeFormikValues = typeof initialValues;

/** Step 2 in the Twilio verification system. This step requires the user to enter in a code sent to their phone/email.  */
export const VerifyCodeForm = ({
  data,
  initialStep,
  channel,
  userId,
  handleCloseDialog,
  setStep,
  onVerifySuccess,
  createAccountData,
  autoLoginOnVerificationSuccess,
}: {
  data: string;
  initialStep: VerificationStepEnum;
  channel: TwilioVerificationChannelEnum;
  userId?: number;
  handleCloseDialog: () => void;
  setStep: React.Dispatch<React.SetStateAction<VerificationStepEnum>>;
  onVerifySuccess?: (data: APIResponse<{ USER: User }>) => void;
  createAccountData?: Pick<TwilioCheckCodeCreateAccountValues, 'first_name' | 'last_name' | 'password'>;
  autoLoginOnVerificationSuccess: boolean;
}) => {
  const userQuery = useUserQuery({
    isReloadUser: true,
    enabled: false,
    hasToastsEnabled: false,
  });
  const checkCodeMutation = useTwilioCheckCodeMutation();
  const sendCodeMutation = useTwilioSendCodeMutation();
  const [codeInputKey, setCodeInputKey] = React.useState(['codeInputKey', 0]);

  const resendCode = () => sendCodeMutation.mutate({ value: data, channel });

  const resendCodeByCall = () => sendCodeMutation.mutate({ value: data, channel: TwilioVerificationChannelEnum.call });

  const onSubmit = (values: typeof initialValues, helpers: FormikHelpers<typeof initialValues>) => {
    let sendData: TwilioCheckCodeSendValues = { value: data, code: values.code, channel };
    if (createAccountData) {
      sendData = { ...sendData, ...createAccountData };
    }
    if (autoLoginOnVerificationSuccess) {
      sendData = { ...sendData, auto_login_user: autoLoginOnVerificationSuccess };
    }

    checkCodeMutation.mutate(sendData, {
      onSuccess: async (data: APIResponse<{ USER: User; }>) => {
        if (userId) {
          await userQuery.refetch().catch(e => Sentry.captureException(e));
        }
        handleCloseDialog();
        onVerifySuccess && onVerifySuccess(data);
      },
      onError: () => {
        helpers.setFieldError('code', 'Code is incorrect');
      },
    });
  };

  const formattedData = useMemo(() => {
    if (channel === TwilioVerificationChannelEnum.sms) {
      return formatPhoneNumber(data);
    }

    return data;
  }, [channel, data]);

  const handleBack = () => {
    if (initialStep === VerificationStepEnum.VerifyCode) {
      handleCloseDialog();
    } else {
      setStep(VerificationStepEnum.SendCode);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={yup.object({ code: yup.string().required(messages.requiredValue) })}
      onSubmit={onSubmit}
    >
      {({ isValid, values, setFieldValue }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <img src={verifyImage} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary">
                Please Confirm Code
              </Typography>
              <Typography variant="body2">
                We have sent a 6-digit code to <strong>{formattedData}</strong>. You should receive the message within a
                few seconds.
              </Typography>
            </Grid>

            <VerifyCodeInput key={codeInputKey.join('')} />

            <ButtonWrapper item xs={12}>
              <LoadingButton
                onClick={() => {
                  resendCode();
                  setFieldValue('code', '');
                  // When we resend the code, we want to restart the input to make it easy for the user to restart
                  setCodeInputKey(prev => [prev[0], Number(prev[1]) + 1]);
                }}
                isLoading={sendCodeMutation.isLoading}
                variant="text"
                size="small"
              >
                {channel === TwilioVerificationChannelEnum.sms ? 'Resend the SMS Code' : null}
                {channel === TwilioVerificationChannelEnum.email ? 'Resend the Email Code' : null}
              </LoadingButton>
            </ButtonWrapper>
            <Grid item xs={12}>
              {channel === TwilioVerificationChannelEnum.sms ? (
                <LoadingButton
                  onClick={resendCodeByCall}
                  isLoading={sendCodeMutation.isLoading}
                  variant="text"
                  size="small"
                >
                  Call me with the code
                </LoadingButton>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <StepperControlsFormik
                handleBack={handleBack}
                nextText="Verify"
                backText={initialStep === VerificationStepEnum.VerifyCode ? 'Close' : 'Back'}
                isLoading={checkCodeMutation.isLoading}
                isDisabled={!isValid || values.code.length !== 6}
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
